import React, {useMemo, useRef, useState} from 'react';
import {Modal} from "../../../../../../components/modal/modal.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../../utils/aggrid-utils.js";
import citizenService from "../../../../../../services/web/citizen-service.js";
import projectUtils from "../../../../../../utils/project-utils.js";
import {FaCheck, FaFileExcel} from "react-icons/fa";
import {FaTriangleExclamation} from "react-icons/fa6";
import CitizenImportCellRenderer from "./citizen-import-cell-renderer.jsx";
import CitizenImportLegend from "./citizen-import-legend.jsx";
import Button from "../../../../../../components/button/button.jsx";
import {toast} from "react-toastify";
import {Menu, MenuButton, MenuItem, MenuItems, Transition} from "@headlessui/react";

const acceptedFileTypes = [
    'text/csv',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const CitizenImport = ({open, setOpen, refetch, setIsLoading, programs}) => {
    const [file, setFile] = useState(undefined)
    const [citizens, setCitizens] = useState([]);
    const [columnDefs, setColumnDefs] = useState([])

    const gridRef = useRef(undefined);

    const getPreviewErrorMessage = (error) => {
        const messages = {
            "sos.citizen.import.rows.limit.exceeded": "A planilha excede o limite de 1.000 linhas.",
            "invalid.file.type": "Selecione um arquivo Excel válido.",
            "invalid.file": "O arquivo Excel é inválido ou não pode ser lido."
        };

        return messages[error.response?.data?.message] ?? "Não foi possível gerar a prévia da importação.";
    };

    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);

    const onClick = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = acceptedFileTypes.join(',');
        input.onchange = event => createImportPreview(event.target.files[0])
        input.click();
    }

    const onDrop = (e) => {
        e.preventDefault();

        let file;

        if (e.dataTransfer.items) {
            const items = [...e.dataTransfer.items];
            const firstFileIndex = items.findIndex(item => item.kind === 'file');
            file = items[firstFileIndex]?.getAsFile();
        } else {
            file = [...e.dataTransfer.files][0];
        }

        createImportPreview(file);
    }

    const createImportPreview = (file) => {
        if (!file || !acceptedFileTypes.includes(file.type)) return;

        setIsLoading(true)

        const formData = new FormData();
        formData.append('file', file);

        citizenService.importPreview(formData)
            .then(({data}) => {
                if (!data?.length) {
                    projectUtils.swalErrorMessage(
                        "",
                        "A planilha não possui linhas preenchidas para importação.",
                        "Ok"
                    );
                    return;
                }

                handleColumns(data);
                setCitizens(data);
                setFile(file);
            })
            .catch(error => {
                projectUtils.swalErrorMessage("", getPreviewErrorMessage(error), "Ok");
                console.error(error);
            })
            .finally(() => setIsLoading(false));
    }

    const handleColumns = (data) => {
        if (!data?.length) {
            setColumnDefs([]);
            return;
        }

        const {citizen, type} = data[0];
        const columns = [];

        const formaterDate = (date) => {
            if (!date) return "";

            const _date = new Date(date + " 00:00").toLocaleDateString();

            if (/^\d{2}\/\d{2}\/\d{4}$/.test(_date)) return _date

            return date
        }

        if (citizen?.citizenInfo) {
            const isCPF = type === "CPF";

            columns.push(
                {
                    headerName: "",
                    field: "messages",
                    width: 90,
                    cellRenderer: ({value}) => {
                        return (
                            <div className={"flex items-center justify-center h-full"}>
                                {
                                    value.length > 0 ? <FaTriangleExclamation color={"#ed5565"}/> : <FaCheck color={"#219453"}/>
                                }
                            </div>
                        )
                    },
                    hozAlign: "center",
                    sortable: false
                },
                {
                    headerName: isCPF ? "Identificação do protegido" : "Identificação da empresa",
                    children: [
                        {headerName: "Programa de proteção", field: "citizen.citizenInfo.program"},
                        {headerName: "Nome", field: "citizen.citizenInfo.name", hide: !isCPF},
                        {headerName: "Nome social", field: "citizen.citizenInfo.socialName", hide: !isCPF},
                        {headerName: "CPF", field: "citizen.citizenInfo.cpf", valueFormatter: ({value}) => projectUtils.formatCpfToView(value), hide: !isCPF},
                        {headerName: "Data de nascimento", field: "citizen.citizenInfo.dtBirth", valueFormatter: ({value}) => formaterDate(value), hide: !isCPF},
                        {headerName: "Sexo", field: "citizen.citizenInfo.sex", hide: !isCPF},
                        {headerName: "Razão social", field: "citizen.citizenInfo.name", hide: isCPF},
                        {headerName: "Nome fantasia", field: "citizen.citizenInfo.aliasName", hide: isCPF},
                        {headerName: "CNPJ", field: "citizen.citizenInfo.cnpj", hide: isCPF},
                        {headerName: "Telefone", field: "citizen.citizenInfo.phone", valueFormatter: ({value}) => projectUtils.formatPhone(value)},
                        {headerName: "E-mail", field: "citizen.citizenInfo.email"},
                        {headerName: "Número de identificação", field: "citizen.citizenInfo.identificationNumber"},
                        {headerName: "Grau de risco", field: "citizen.citizenInfo.lifeRisk", valueFormatter: ({data}) => data.citizen.citizenInfo.lifeRiskLang},
                        {headerName: "Tipo de local", field: "citizen.citizenInfo.address_addressType", valueFormatter: ({data}) => data.citizen.citizenInfo.address_addressTypeLang},
                        {headerName: "Logradouro", field: "citizen.citizenInfo.address_address"},
                        {headerName: "Número", field: "citizen.citizenInfo.address_number"},
                        {headerName: "Bairro", field: "citizen.citizenInfo.address_neighborhood"},
                        {headerName: "Complemento", field: "citizen.citizenInfo.address_complement"},
                        {headerName: "Município", field: "citizen.citizenInfo.address_city"},
                        {headerName: "Estado (UF)", field: "citizen.citizenInfo.address_district"},
                        {headerName: "CEP", field: "citizen.citizenInfo.address_zipCode"},
                        {headerName: "Identificação", field: "citizen.citizenInfo.address_description"},
                        {headerName: "Latitude", field: "citizen.citizenInfo.address_latitude"},
                        {headerName: "Longitude", field: "citizen.citizenInfo.address_longitude"},
                        {headerName: "Observação", field: "citizen.citizenInfo.observation"},
                    ]
                })
        }

        if (citizen?.protectiveMeasure) {
            columns.push({
                headerName: "Dados adicionais",
                children: [
                    {headerName: "Número de filhos", field: "citizen.protectiveMeasure.childCount"},
                    {headerName: "Escolaridade", field: "citizen.protectiveMeasure.educationId", valueFormatter: ({data}) => data.citizen.protectiveMeasure.educationIdLang},
                    {headerName: "Nacionalidade", field: "citizen.protectiveMeasure.nationality"},
                    {headerName: "Raça ou etnia", field: "citizen.protectiveMeasure.raceEthnicityId", valueFormatter: ({data}) => data.citizen.protectiveMeasure.raceEthnicityIdLang},
                    {headerName: "Religião", field: "citizen.protectiveMeasure.religionId", valueFormatter: ({data}) => data.citizen.protectiveMeasure.religionIdLang},
                    {headerName: "Naturalidade", field: "citizen.protectiveMeasure.cityBirth"},
                ]
            })
        }

        if (citizen?.aggressor) {
            columns.push({
                headerName: "Agressor",
                children: [
                    {headerName: "Nome", field: "citizen.aggressor.name"},
                    {headerName: "Nome social", field: "citizen.aggressor.socialName"},
                    {headerName: "Tipo de medida", field: "citizen.aggressor.protectionType", valueFormatter: ({data}) => data.citizen.aggressor.protectionTypeLang},
                    {headerName: "Vínculo com protegido", field: "citizen.aggressor.familyConnectionId", valueFormatter: ({data}) => data.citizen.aggressor.familyConnectionIdLang},
                    {headerName: "Escolaridade", field: "citizen.aggressor.educationId", valueFormatter: ({data}) => data.citizen.aggressor.educationIdLang},
                    {headerName: "Nacionalidade", field: "citizen.aggressor.nationality"},
                    {headerName: "Naturalidade", field: "citizen.aggressor.cityBirth"},
                    {headerName: "CPF", field: "citizen.aggressor.cpf"},
                    {headerName: "Data de nascimento", field: "citizen.aggressor.dtBirth", valueFormatter: ({value}) => formaterDate(value)},
                    {headerName: "Número do processo", field: "citizen.aggressor.processNumber"},
                    {headerName: "Data de expiração", field: "citizen.aggressor.dtExpiration", valueFormatter: ({value}) => formaterDate(value)},
                    {headerName: "Data de expedição", field: "citizen.aggressor.dtExpedition", valueFormatter: ({value}) => formaterDate(value)},
                    {headerName: "Informações adicionais", field: "citizen.aggressor.description"},
                    {headerName: "Tipo de local", field: "citizen.aggressor.address_addressType", valueFormatter: ({data}) => data.citizen.aggressor.address_addressTypeLang},
                    {headerName: "Logradouro", field: "citizen.aggressor.address_address"},
                    {headerName: "Número", field: "citizen.aggressor.address_number"},
                    {headerName: "Bairro", field: "citizen.aggressor.address_neighborhood"},
                    {headerName: "Complemento", field: "citizen.aggressor.address_complement"},
                    {headerName: "Município", field: "citizen.aggressor.address_city"},
                    {headerName: "Estado (UF)", field: "citizen.aggressor.address_district"},
                    {headerName: "CEP", field: "citizen.aggressor.address_zipCode"},
                    {headerName: "Identificação", field: "citizen.aggressor.address_description"},
                    {headerName: "Latitude", field: "citizen.aggressor.address_latitude"},
                    {headerName: "Longitude", field: "citizen.aggressor.address_longitude"},
                ]
            })
        }

        setColumnDefs(columns)
    }

    const defaultColDef = useMemo(() => ({
        cellRenderer: CitizenImportCellRenderer,
    }), []);

    const cleanImport = () => {
        setFile(undefined);
        setCitizens([]);
    }

    const confirmImport = () => {
        if (!file) return;

        setIsLoading(true)

        const formData = new FormData();
        formData.append('file', file);

        citizenService.import(formData)
            .then(({data}) => {
                if (data?.errors && data?.success) {
                    toast.success(`Cidadãos salvos: ${data.success} <br /> Cidadãos com erro: ${data.errors}`);
                } else if (data?.errors && !data?.success) {
                    toast.error("Erro ao salvar.");
                } else {
                    toast.success("Salvo com sucesso");
                }

                cleanImport();

                setOpen(false)
                refetch();
            })
            .catch(error => {
                projectUtils.swalErrorMessage('', 'Não foi possível importar os cidadãos.', 'Ok');
                console.log(error);
            })
            .finally(() => setIsLoading(false));
    }

    return (
        <Modal open={open} setOpen={setOpen} title={"Importação de cidadãos"}>
            <Modal.Body className={"!mt-0 p-4"}>
                <div className={"h-[calc(100vh-240px)]"}>
                    {
                        !file &&
                        <div className={"h-full flex justify-center items-center border border-dashed cursor-pointer select-none"} onClick={onClick} onDrop={onDrop} onDragOver={(e) => e.preventDefault()}>
                            <h2 className={"text-2xl"}>Clique aqui ou arraste o arquivo para fazer a importação.</h2>
                        </div>
                    }

                    {
                        file &&
                        <>
                            <div className={"ag-theme-quartz grid h-[calc(100%-30px)]"}>
                                <AgGridReact
                                    ref={gridRef}
                                    rowHeight={32}
                                    rowData={citizens ?? []}
                                    columnDefs={columnDefs}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    defaultColDef={defaultColDef}
                                    theme={theme}
                                />
                            </div>

                            <CitizenImportLegend citizens={citizens}/>
                        </>
                    }
                </div>
            </Modal.Body>
            <Modal.Footer className={"!justify-between"}>
                <div className={"flex gap-3"}>
                    <Menu as="div" className="flex items-center text-white">
                        <MenuButton type="button" className={"flex justify-center cursor-pointer h-[32px] items-center gap-1 focus:ring-2 focus:outline-none font-medium rounded shadow text-[13px] px-4 py-2 text-center text-white bg-green-600 hover:bg-green-700 focus:ring-green-300"}>
                            <FaFileExcel /> Baixar modelo (CPF)
                        </MenuButton>
                        <Transition
                            enter="transition ease-out duration-75"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <MenuItems
                                anchor="top end"
                                className="origin-top-right rounded-md shadow border text-[13px] bg-white [--anchor-gap:var(--spacing-3)] focus:outline-none z-[100] text-gray-700 flex flex-col border-[#bdbdbd] gap-1 p-1 items-start"
                            >
                                {
                                    programs?.map(p => (
                                        <MenuItem key={p.value}>
                                            <a href={API_KEEPER + `/sos/citizen/import/template/CPF/${p.value}`}
                                               className={"cursor-pointer p-1 rounded-sm hover:bg-[#d3d3d3] w-[200px] truncate"}
                                               title={p.label}
                                            >
                                                {p.label}
                                            </a>
                                        </MenuItem>
                                    ))
                                }
                            </MenuItems>
                        </Transition>
                    </Menu>
                    <Menu as="div" className="flex items-center text-white">
                        <MenuButton type="button" className={"flex justify-center cursor-pointer h-[32px] items-center gap-1 focus:ring-2 focus:outline-none font-medium rounded shadow text-[13px] px-4 py-2 text-center text-white bg-green-600 hover:bg-green-700 focus:ring-green-300"}>
                            <FaFileExcel /> Baixar modelo (CNPJ)
                        </MenuButton>
                        <Transition
                            enter="transition ease-out duration-75"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <MenuItems
                                anchor="top end"
                                className="origin-top-right rounded-md shadow border text-[13px] bg-white [--anchor-gap:var(--spacing-3)] focus:outline-none z-[100] text-gray-700 flex flex-col border-[#bdbdbd] gap-1 p-1 items-start"
                            >
                                {
                                    programs?.map(p => (
                                        <MenuItem key={p.value}>
                                            <a href={API_KEEPER + `/sos/citizen/import/template/CNPJ/${p.value}`}
                                               className={"cursor-pointer p-1 rounded-sm hover:bg-[#d3d3d3] w-[200px] truncate"}
                                               title={p.label}
                                            >
                                                {p.label}
                                            </a>
                                        </MenuItem>
                                    ))
                                }
                            </MenuItems>
                        </Transition>
                    </Menu>
                </div>

                <div className={"flex gap-3"}>
                    <Button target={"secondary"} onClick={cleanImport}>
                        Limpar seleção
                    </Button>
                    <Button target={"primary"} onClick={confirmImport}>
                        Confirmar importação
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default CitizenImport;